import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import StatusBadge, { URGENCY } from "@/components/StatusBadge";
import FileUploader from "@/components/FileUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, DollarSign, Clock, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5">
    <div className="flex items-center justify-between">
      <p className="text-sm text-slate-500 font-medium">{label}</p>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone === "amber" ? "bg-orange-100 text-sunburst" : "bg-slate-100 text-[#0B192C]"}`}><Icon className="w-5 h-5" /></div>
    </div>
    <p className="mt-2 text-3xl font-display font-black text-slate-900 font-mono">{value}</p>
  </div>
);

export default function PMDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_address: "", service_type: "", description: "", urgency: "normal", photos: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, o, t] = await Promise.all([api.get("/stats"), api.get("/work-orders"), api.get("/meta/service-types")]);
    setStats(s.data); setOrders(o.data); setTypes(t.data.service_types);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type) return toast.error("Select a service type");
    setBusy(true);
    try {
      await api.post("/work-orders", form);
      toast.success("Work order submitted!");
      setOpen(false);
      setForm({ property_address: "", service_type: "", description: "", urgency: "normal", photos: [] });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <DashboardShell>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-black text-slate-900">Your Work Orders</h1>
          <p className="text-slate-500 text-sm mt-1">Submit requests and track them to completion.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-work-order-button" className="bg-sunburst hover:bg-orange-600 text-white font-semibold"><Plus className="w-4 h-4 mr-1" />New Work Order</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display">Submit a Work Order</DialogTitle></DialogHeader>
            <form data-testid="work-order-submit-form" onSubmit={submit} className="space-y-4">
              <div>
                <Label>Property address</Label>
                <Input data-testid="work-order-property-address-input" required value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })} className="mt-1.5" placeholder="123 Palm Ave, Miami, FL" />
              </div>
              <div>
                <Label>Service type</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger data-testid="work-order-service-type-select" className="mt-1.5"><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="work-order-description-input" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={3} placeholder="Describe the issue…" />
              </div>
              <div>
                <Label>Urgency</Label>
                <RadioGroup data-testid="work-order-urgency-radio" value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })} className="mt-2 grid grid-cols-2 gap-2">
                  {["low", "normal", "high", "emergency"].map((u) => (
                    <label key={u} className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer capitalize text-sm has-[:checked]:border-sunburst has-[:checked]:bg-orange-50">
                      <RadioGroupItem value={u} /> {u}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label>Photos (optional)</Label>
                <div className="mt-1.5">
                  <FileUploader testid="work-order-photo-upload" label="Add property photos" accept="image/*" multiple
                    onUploaded={(d) => setForm((f) => ({ ...f, photos: [...f.photos, d.storage_path] }))} />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="work-order-submit-confirm" className="bg-[#0B192C] hover:bg-[#1E3E62] text-white w-full">{busy ? "Submitting…" : "Submit work order"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {stats && (
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Clock} label="Awaiting Your Approval" value={stats.awaiting_approval} tone="amber" />
          <Stat icon={FileText} label="Active Jobs" value={stats.in_progress} />
          <Stat icon={CheckCircle2} label="Completed" value={stats.completed} />
          <Stat icon={DollarSign} label="Total Paid" value={`$${stats.total_billed}`} tone="amber" />
        </div>
      )}

      <div className="mt-8 grid gap-4">
        {orders.map((o) => (
          <div key={o.id} data-testid={`pm-wo-card-${o.ref}`} onClick={() => navigate(`/orders/${o.id}`)}
            className="bg-white rounded-2xl border border-slate-200 p-5 cursor-pointer hover:border-sunburst hover:shadow-md transition-all flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-semibold text-ocean">{o.ref}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCY[o.urgency]?.cls}`}>{URGENCY[o.urgency]?.label}</span>
              </div>
              <p className="font-display font-semibold text-slate-900 mt-1">{o.service_type}</p>
              <p className="text-sm text-slate-400 truncate">{o.property_address}</p>
            </div>
            <div className="flex items-center gap-6">
              {o.pm_price != null && (
                <div className="text-right">
                  <p className="text-xs text-slate-400">All-inclusive price</p>
                  <p className="font-mono text-xl font-bold text-slate-900">${o.pm_price}</p>
                </div>
              )}
              <StatusBadge status={o.status} />
            </div>
          </div>
        ))}
        {orders.length === 0 && <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 text-slate-400">No work orders yet. Submit your first one!</div>}
      </div>
    </DashboardShell>
  );
}
